import {
  ActionUrl,
  CLEAR_CLASS_NAME,
  CLOSE_CLASS_NAME,
  EDIT_PROJECT_CLASS_NAME,
  GROUP_CLASS_NAME,
  PROJECT_LIST_CLASS_NAME,
  PROJECT_REMOVE_CLASS_NAME,
} from "../constants";
import {Api, Method} from "../../api/api";
import {showToast} from "../../toast";
import {renderElement} from "../../utils/common";
import {getEditProjectModal, getLastMoreProjectElement, getProjectTagTemplate, getProjectTemplate} from "../template";

/**
 * Функционал редактирования проектов на странице меток. В конструктор передаётся элемент группы меток в котором происходит редактирование проектов.
 */
class EditProject {
  #parent;
  #currentProjectListElement;
  #currentActiveProjectElements;
  #checkedProjects;
  #groupId;
  #modalElement;
  #formElement;
  #checkboxElements;
  #checkboxParentElements;
  #modalProjectListElement;
  #searchInputElement;

  #api = new Api();
  #projectsUrl = ActionUrl.GET_PROJECTS_URL;
  #hiddenClass = `list__item--hidden`;
  #visibleProjectAmount = 6;

  constructor() {
    this.onDocumentClick = this.onDocumentClick.bind(this);

    document.addEventListener(`click`, this.onDocumentClick);
  }

  /**
   * Инициализация
   */
  #init() {
    this.#groupId = +this.#parent.dataset.id;
    this.#currentProjectListElement = this.#parent.querySelector(`.${PROJECT_LIST_CLASS_NAME}`);
    this.#currentActiveProjectElements = Array.from(this.#currentProjectListElement.querySelectorAll(`li[data-id]`));

    this.modalElementClickHandler = this.modalElementClickHandler.bind(this);
    this.formElementChangeHandler = this.formElementChangeHandler.bind(this);
    this.searchInputHandler = this.searchInputHandler.bind(this);
    this.formSubmitHandler = this.formSubmitHandler.bind(this);

    void this.#fetchProjects();
  }

  /**
   * Отправка запроса на получение массива с объектами всех проектов
   * @returns {Promise<void>}
   */
  async #fetchProjects() {
    const {code, answer} = await this.#api.customQuery(this.#projectsUrl, {});

    if (code === 200) {
      this.#renderModal(answer.data);
    } else {
      showToast(answer.text);
    }
  }

  /**
   * Генерация окна редактирования проектов, присвоение переменных и добавление слушателей.
   * @param projects {Array<Object>} - массив с объектами проектов
   */
  #renderModal(projects) {
    this.#modalElement = renderElement(document.body, getEditProjectModal(projects, this.#groupId, this.#currentActiveProjectElements));

    if (this.#modalElement) {
      this.#formElement = this.#modalElement.querySelector(`form`);
      this.#checkboxElements = Array.from(this.#modalElement.querySelectorAll(`input[data-name]`));
      this.#modalProjectListElement = this.#modalElement.querySelector(`.${PROJECT_LIST_CLASS_NAME}`);
      this.#checkboxParentElements = Array.from(this.#modalElement.querySelectorAll(`li[data-name]`));
      this.#searchInputElement = this.#modalElement.querySelector(`input[type="search"]`);

      this.#updateCheckboxStatuses();

      this.#modalElement.addEventListener(`click`, this.modalElementClickHandler);
      this.#formElement.addEventListener(`change`, this.formElementChangeHandler);
      this.#searchInputElement.addEventListener(`input`, this.searchInputHandler);
      this.#formElement.addEventListener(`submit`, this.formSubmitHandler);
    }
  }

  /**
   * Приведение необходимых чекбоксов в активное состояние после генерации окна редактирования проектов
   */
  #updateCheckboxStatuses() {
    this.#currentActiveProjectElements.forEach((project) => {
      this.#checkboxElements.find((checkbox) => +checkbox.value === +project.dataset.id).checked = true;
    });
  }

  /**
   * Удаление окна редактирования проектов
   */
  #removeModal() {
    this.#modalElement.remove();
  }

  /**
   * Удаление или генерация элемента проекта после активации или деактивации чекбокса
   * @param isChecked {boolean} - активирован ли чекбокс
   * @param id {string} - идентификатор
   * @param name {string} - имя проекта
   */
  #updateProjectList(isChecked, id, name) {
    if (isChecked) {
      renderElement(this.#modalProjectListElement, getProjectTagTemplate(id, name));
    } else {
      Array.from(this.#modalProjectListElement.querySelectorAll(`li`))
        .find((project) => project.dataset.id === id)
        .remove();
    }
  }

  /**
   * Удаление необходимого проекта из списка и деактивация соответствующего чекбокса
   * @param item {HTMLElement} - элемент который требуется удалить
   */
  #removeOneProject(item) {
    item.remove();
    this.#checkboxElements.find((checkbox) => checkbox.value === item.dataset.id).checked = false;
  }

  /**
   * Добавление всех проектов из списка
   */
  #removeAllProjects() {
    this.#modalProjectListElement.innerHTML = ``;
  }

  /**
   * Добавление всех проектов в список
   */
  #addAllProjects() {
    this.#removeAllProjects();

    this.#checkboxElements.forEach((checkbox) =>
      renderElement(this.#modalProjectListElement, getProjectTagTemplate(checkbox.value, checkbox.dataset.name)),
    );
  }

  /**
   * Удаление или добавление всех элементов проекта в список проектов в окне редактирования
   * @param areAllChecked {boolean} - Активированы ли все чекбоксы
   */
  #setStatusToAllProjects(areAllChecked) {
    if (areAllChecked) {
      this.#addAllProjects();
    } else {
      this.#removeAllProjects();
    }
  }

  /**
   * Активация или деактивация всех чекбоксов в форме
   * @param areAllChecked {boolean} - Активированы ли все чекбоксы
   */
  #setStatusToAllCheckboxes(areAllChecked) {
    this.#checkboxElements.forEach((checkbox) => (checkbox.checked = areAllChecked));
  }

  /**
   * Фильтрация найденных элементов после поиска
   * @param value {string} - строка поиска
   */
  #filterSearchedItems(value) {
    this.#checkboxParentElements.forEach((parent) => {
      if (!value.length) {
        parent.classList.remove(this.#hiddenClass);
        return;
      }

      if (parent.dataset.name.trim().toLowerCase().includes(value.trim().toLowerCase())) {
        parent.classList.remove(this.#hiddenClass);
      } else {
        parent.classList.add(this.#hiddenClass);
      }
    });
  }

  /**
   * Очистка поля с поиском
   */
  #resetSearchValue() {
    this.#searchInputElement.value = ``;
    this.#searchInputElement.dispatchEvent(new Event(`input`));
  }

  /**
   * Получение данных формы
   *
   * @returns {FormData}
   */
  #getProjectsData() {
    return {
      id: this.#groupId,
      projects: this.#checkedProjects.map((project) => +project.value),
    };
  }

  /**
   *  Отправка запроса на изменение проектов меток
   */
  async #postData() {
    const {code, answer} = await this.#api.customQuery(this.#formElement.action, {
      method: Method.POST,
      query: JSON.stringify(this.#getProjectsData()),
    }); // TODO: Поменять на POST

    if (code === 200) {
      this.#updateCurrentActiveProjects();
      this.#removeModal();
    } else {
      showToast(answer.text);
    }
  }

  /**
   * Обновление списка активных проектов
   */
  #updateCurrentActiveProjects() {
    this.#currentProjectListElement.innerHTML = ``;

    this.#checkedProjects.forEach((project, index) => {
      renderElement(this.#currentProjectListElement, getProjectTemplate(project.dataset.name, project.value, index >= this.#visibleProjectAmount));
    });

    if (this.#checkedProjects.length > this.#visibleProjectAmount) {
      renderElement(this.#currentProjectListElement, getLastMoreProjectElement(this.#checkedProjects.length - this.#visibleProjectAmount));
    }
  }

  /**
   * Были ли произведены изменения в списке проектов
   *
   * @returns {boolean}
   */
  #isProjectsModified() {
    this.#checkedProjects = this.#checkboxElements.filter((checkbox) => checkbox.checked);
    const sortedCurrentProjects = this.#currentActiveProjectElements.map((item) => item.dataset.id).sort();
    const sortedCheckedProjects = this.#checkedProjects.map((item) => item.value).sort();

    if (sortedCurrentProjects.length !== sortedCheckedProjects.length) {
      return true;
    }

    for (let i = 0; i < sortedCurrentProjects.length; i++) {
      if (sortedCheckedProjects[i] !== sortedCurrentProjects[i]) {
        return true;
      }
    }

    return false;
  }

  /**
   * Слушатель ввода поля с поиском
   * @param event
   */
  searchInputHandler(event) {
    this.#filterSearchedItems(event.currentTarget.value);
  }

  /**
   * Слушатель отправки формы
   * @param event
   */
  formSubmitHandler(event) {
    event.preventDefault();

    if (this.#isProjectsModified()) {
      void this.#postData();
    } else {
      this.#removeModal();
    }
  }

  /**
   * Слушатель изменения инпутов в форме редактирования проекта
   * @param event
   */
  formElementChangeHandler(event) {
    const target = event.target;

    if (target.type === `checkbox`) {
      if (target.name !== `check-all`) {
        this.#updateProjectList(target.checked, target.value, target.dataset.name);
      } else {
        this.#setStatusToAllProjects(target.checked);
        this.#setStatusToAllCheckboxes(target.checked);
      }
    }
  }

  /**
   * Слушатель клика на элементе окна редактирования проекта
   * @param event
   */
  modalElementClickHandler(event) {
    const target = event.target;

    if (target.classList.contains(CLOSE_CLASS_NAME) || target === this.#modalElement) {
      this.#removeModal();
    } else if (target.classList.contains(PROJECT_REMOVE_CLASS_NAME)) {
      this.#removeOneProject(target.closest(`li[data-id]`));
    } else if (target.classList.contains(CLEAR_CLASS_NAME)) {
      this.#resetSearchValue();
    }
  }

  onDocumentClick({target}) {
    if (target.classList.contains(EDIT_PROJECT_CLASS_NAME)) {
      this.#parent = target.closest(`.${GROUP_CLASS_NAME}`);

      this.#init();
    }
  }
}

export {EditProject};
